import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getDemoScenario, undeployPackages } from '../../../config/demoScenarios';
import {
  designInitialConnections,
  designInitialProjectPackages,
} from '../../../config/designFixtures';
import { isDesignCanvasProject } from '../../../config/designMode';
import { getWorkflowTemplate } from '../../../config/workflowTemplates';
import { useAuth } from '../../../contexts/Auth';
import useApi from '../../../hooks/useAPI';

const getApiUrl = () => process.env.REACT_APP_API_URL || 'http://localhost:8001';

const useProjectData = (projectIdArg) => {
  const params = useParams();
  const location = useLocation();
  const projectId = projectIdArg || params.projectId;
  const fresh = location.pathname.startsWith('/demo');
  const { token } = useAuth();
  const { projects: projectsApi } = useApi();
  const [projectData, setProjectData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchRef = useRef(null);
  const wsRef = useRef(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const demo = fresh ? getDemoScenario(projectId) : null;
      if (demo) {
        if (fetchRef.current !== fetchData) return;
        setProjectData({
          packages: undeployPackages(demo.packages),
          connections: demo.connections,
        });
        setError(null);
        return;
      }
      if (isDesignCanvasProject(projectId)) {
        if (fetchRef.current !== fetchData) return;
        const template = getWorkflowTemplate(projectId);
        const packages = template
          ? template.packages
          : designInitialProjectPackages;
        const connections = template
          ? template.connections
          : designInitialConnections;
        setProjectData({
          packages: fresh ? undeployPackages(packages) : packages,
          connections,
        });
        setError(null);
        return;
      }

      const [packagesResponse, connectionsResponse] = await Promise.all([
        projectsApi.listProjectPackages(projectId, token),
        projectsApi.listConnections(projectId, token),
      ]);

      if (fetchRef.current !== fetchData) return;

      setProjectData({
        packages: packagesResponse.body,
        connections: connectionsResponse.body,
      });
      setError(null);
    } catch (err) {
      setError('Failed to load project data');
      console.error('Error fetching project data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, fresh, token, projectsApi]);

  useEffect(() => {
    fetchRef.current = fetchData;
    fetchData();

    // const cleanup = setupWebSocket();

    return () => {
      fetchRef.current = null;
      // cleanup();
    };
  }, [fetchData]);

  const refreshData = useCallback(() => {
    // if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
    //   wsRef.current.send(JSON.stringify({ type: 'request_update' }));
    // } else {
    //   fetchData();
    // }
    fetchData();
  }, [fetchData]);

  return { projectData, isLoading, error, refreshData };
};

export default useProjectData;
